import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TuvaToast from '@tuva-ui/components/src/TuvaToast/TuvaToast';
import Input from '@tuva-ui/components/src/Input/Input';
import Form from '@tuva-ui/components/src/Form/Form';
import TuvaModal from '@tuva-ui/components/src/TuvaModal/index';
import Loading from '@tuva-ui/components/src/Loading/Loading';

import Constants from '../util/Constants';
import ModalService from '../services/Modal.service';

const SUBSCRIPTION_TYPES = [
  { value: 'jr', display: 'Tuva Jr.' },
  { value: 'simulation', display: 'Tuva Sims' },
  { value: 'premium', display: 'Tuva Premium' },
  { value: 'ose_companion', display: 'OpenSciEd Companion' },
  { value: 'assessments', display: 'Assessments' },
];

// The modal can be opened with a preselected tier slug (e.g. 'simulation' from
// the Tuva Sims plan card). The checkboxes are keyed by display name, so the
// incoming slug is translated into the matching display name(s).
const getDefaultSubscriptions = subscription => {
  const values = Array.isArray(subscription)
    ? subscription
    : String(subscription || '')
        .split(',')
        .map(value => value.trim());

  return SUBSCRIPTION_TYPES.filter(
    type => values.includes(type.value) || values.includes(type.display),
  ).map(type => type.display);
};

// Required-field label with a red asterisk (matches the mockup's `.req`).
const req = label => (
  <>
    {label} <span className="req-star">*</span>
  </>
);

function PricingModal({ onCancel, subscription }) {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState();
  const [roles, setRoles] = useState();
  const [subscriptionPlan, setSubscriptionPlan] = useState();
  const [interestedContent, setInterestedContent] = useState();

  // Kept stable across renders — Form (isDynamic) compares defaultValue by
  // reference and would loop on a fresh array every render.
  const defaultSubscriptions = useMemo(
    () => getDefaultSubscriptions(subscription),
    [subscription],
  );

  let getRoleKey = value => {
    if (value) {
      let index = roles.findIndex(o => o['role_name'] === value);
      return roles[index]['role_value'];
    }
  };

  let populateInterestedContent = value => {
    let content = [];
    if (value === 'Per School / Campus Plan') {
      content.push(Constants.INTERESTED_CONTENT_OPTIONS[3]);
    } else if (value === 'Districtwide Plan') {
      content.push(Constants.INTERESTED_CONTENT_OPTIONS[3]);
      content.push(Constants.INTERESTED_CONTENT_OPTIONS[4]);
    }
    setInterestedContent(content);
  };

  let getSubscriptionTitle = () => {
    let subscriptionTitle = '';
    if (subscriptionPlan === 'Per Teacher Plan') {
      subscriptionTitle = 'For Teachers';
    } else if (subscriptionPlan === 'Per School / Campus Plan') {
      subscriptionTitle = 'For Per School / Campus Plan';
    } else if (subscriptionPlan === 'Districtwide Plan') {
      subscriptionTitle = 'For Districtwide Plan';
    }
    return subscriptionTitle;
  };

  let whoamiAPI = useCallback(() => {
    return new Promise((resolve, reject) => {
      ModalService.whoami()
        .then(userInfo => {
          resolve(userInfo);
        })
        .catch(err => {
          resolve(null);
          console.log('Error while fetching user details', err);
        });
    });
  }, []);

  let getRoles = useCallback(() => {
    return new Promise((resolve, reject) => {
      ModalService.getRoles()
        .then(function (data) {
          resolve(data);
        })
        .catch(function (err) {
          resolve(null);
          console.log('Error while fetching roles', err);
        });
    });
  }, []);

  useEffect(() => {
    const fetchRoles = getRoles();
    const fetchUser = whoamiAPI();

    Promise.all([fetchRoles, fetchUser]).then(responses => {
      if (responses[0]) {
        setRoles(responses[0]);
      }
      if (responses[1]) {
        setUser(responses[1]);
      }
      setIsLoading(false);
    });
  }, [whoamiAPI, getRoles]);

  const onSubmit = useCallback(
    data => {
      data['contentInterested'] = data['contentInterested']?.toString();
      // Multi-select: submitted as a comma separated list of display names,
      // always in the order the checkboxes are listed.
      const selected = Array.isArray(data['subscription'])
        ? data['subscription']
        : [data['subscription']];
      data['subscription'] = SUBSCRIPTION_TYPES.filter(type =>
        selected.includes(type.display),
      )
        .map(type => type.display)
        .join(', ');

      ModalService.saveRequestQuote(data)
        .then(function (response) {
          if (response.fail === 1) {
            TuvaToast.error(
              'We encountered an issue while submitting your request. Please try again later.',
            );
          } else {
            TuvaToast.info(
              "Thank you for your request! We'll get in touch soon.",
            );
          }
          onCancel();
        })
        .catch(function (err) {
          TuvaToast.error(
            'We encountered an issue while submitting your request.',
          );
        });
    },
    [onCancel],
  );

  return (
    <TuvaModal isOpen onClose={e => onCancel()} closeIcon style={{ top: '20px', width: '550px', padding: 0, margin: 0, borderRadius: '14px', transform: 'translateX(-50%)', maxHeight: 'calc(100vh - 40px)' }} className="tuva-common-modal pricing-modal">
      <div className="popup-header border-bottom py-3 px-4">
        <h5 className="font-bold">Request a Quote for a Tuva Subscription</h5>
      </div>
      <div className="py-3 px-4">
        {!isLoading ? (
          <div className="col-12">
            <Form
              submitText={'Submit'}
              cancelText="Cancel"
              layout={Form.LAYOUT.VERTICAL}
              verticalExtraPadding
              onSubmit={onSubmit}
              onCancel={onCancel}
              isDynamic={true}
            >
              <Form.Item
                label={req('Name')}
                name="name"
                defaultValue={
                  user ? user.first_name + ' ' + user.last_name : ''
                }
                rules={[{ required: true }]}
              >
                <Input.Text />
              </Form.Item>
              <Form.Item
                label={req('Email')}
                name="email"
                defaultValue={user?.email}
                rules={[{ required: true }]}
              >
                <Input.Text />
              </Form.Item>
              <Form.Item
                label={req('Organization')}
                name="organization"
                defaultValue={user?.organization}
                rules={[{ required: true }]}
              >
                <Input.Text />
              </Form.Item>
              <Form.Item
                label={req('Role')}
                name="role"
                defaultValue={getRoleKey(user?.role)}
                values={user?.role}
                rules={[{ required: true }]}
              >
                <Input.Select
                  placeholder=" "
                  valueKey="role_name"
                  displayKey="role_name"
                  options={roles}
                />
              </Form.Item>
              <Form.Item
                label={req('Phone')}
                name="phone"
                rules={[{ required: true }]}
              >
                <Input.Text />
              </Form.Item>
              <Form.Item
                label={req('Subscription Type')}
                name="subscription"
                defaultValue={
                  defaultSubscriptions.length ? defaultSubscriptions : ''
                }
                rules={[{ required: true }]}
              >
                <Input.CheckboxGroup type={'vertical'} name="subscriptionGroup">
                  {SUBSCRIPTION_TYPES.map(type => {
                    return (
                      <Input.Checkbox
                        key={type.value}
                        value={type.display}
                        name="subscription"
                      />
                    );
                  })}
                </Input.CheckboxGroup>
              </Form.Item>

              <Form.Item
                label={req('Choose Plan')}
                name="plan"
                rules={[{ required: true }]}
              >
                <Input.RadioGroup type={'vertical'} value={subscriptionPlan}>
                  {Constants.SUBSCRIPTION_OPTIONS.map(function (item, i) {
                    return (
                      <label className={'text-normal'} key={i}>
                        <input
                          className="mr-2"
                          type="radio"
                          checked={subscriptionPlan === item.value}
                          onChange={e => {
                            setSubscriptionPlan(item.value);
                            populateInterestedContent(item.value);
                          }}
                          value={item.value}
                          name="plan"
                        />
                        {item.value}
                      </label>
                    );
                  })}
                </Input.RadioGroup>
              </Form.Item>
              <div className="form-group d-flex mt-1 mb-3 col-12 position-relative ">
                <label className="form-item-label text-normal p2 col-4"></label>
                <div className="text-bold"> {getSubscriptionTitle()}</div>
              </div>

              {subscriptionPlan && (
                <Form.Item
                  label={req(
                    `Number of ${
                      subscriptionPlan.startsWith('Per School')
                        ? 'Campuses'
                        : 'Teachers'
                    }`,
                  )}
                  name="noOfTeachers"
                  rules={[{ required: true }]}
                >
                  <Input.Text />
                </Form.Item>
              )}
              {subscriptionPlan && (
                <Form.Item
                  label={req('Content Area')}
                  name="contentInterested"
                  rules={[{ required: true }]}
                  defaultValue={interestedContent}
                >
                  <Input.CheckboxGroup type={'vertical'} name="contentGroup">
                    {Constants.INTERESTED_CONTENT_OPTIONS.map(option => {
                      return (
                        <Input.Checkbox
                          className={option === 'Other' ? 'mb-5' : ''}
                          value={option}
                          name="content"
                        />
                      );
                    })}
                  </Input.CheckboxGroup>
                </Form.Item>
              )}
              {subscriptionPlan && (
                <div className="form-group d-flex mt-1 mb-3 col-12 position-relative ">
                  <label className="form-item-label text-normal p2 col-4"></label>
                </div>
              )}
              <Form.Item label="Message" name="message">
                <Input.TextArea rows={9} />
              </Form.Item>
            </Form>
          </div>
        ) : (
          <Loading />
        )}
      </div>
    </TuvaModal>
  );
}

export default PricingModal;
